import React, { useState, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Button, CloseIcon, Input, Select } from 'components';
import { UserNameCreateApi } from 'redux/features/LoginSlice';
import { Notification } from 'components/CustomNotification';
import { DateList, MonthList, YearList } from 'assert/OptionList';
import moment from 'moment';
import { usernameRegex } from 'utils/helper';
import { registerPushNotifications } from 'components/RegisterPushNotifications';
import { AuthContext } from 'router/AuthProvider';

const currentDate = new Date();

const UserNameCreation = ({ handleChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { recheckAuth } = useContext(AuthContext);
  
  const { authActionsData } = useSelector((state) => state?.LoginReducer);

  const [loginDetails, setLoginDetails] = useState({
    username: '',
    day: currentDate.getDay(),
    month: currentDate.getMonth(),
    year: currentDate.getFullYear(),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const handleFieldChange = useCallback((data, name) => {
    setLoginDetails((prevState) => ({
      ...prevState,
      [name]: data,
    }));
    if (name === 'username') {
      const trimmed = data.trim();
      if (trimmed.length < 3 || trimmed.length > 50) {
        setUsernameError('Username must be 3–50 characters');
      } else if (!usernameRegex.test(trimmed)) {
        setUsernameError('Only letters, numbers, and underscores are allowed');
      } else {
        setUsernameError('');
      }
    }
  }, []);

  const handleSubmit = () => {
    const { day, month, year, username } = loginDetails;
    if (
      !moment(
        `${year}-${month < 10 ? '0' + month : month}-${
          day < 10 ? '0' + day : day
        }`,
        'YYYY-MM-DD',
        true
      ).isValid()
    ) {
      Notification('error', 'Please enter a valid date');
      return;
    }
    if (moment().diff(moment(`${year}-${month}-${day}`), 'years') < 13) {
      Notification('error', 'Sorry, users must be 13 or older.');
      return;
    }
    setIsLoading(true);
    dispatch(
      UserNameCreateApi(
        { username: username?.trim(), dob: `${year}-${month}-${day}` },
        authActionsData?.data?.userId || authActionsData?.userId
      )
    )
      .then((response) => {
        setIsLoading(false);
        if (response?.success || response?.data?.success) {
          const { token } = response?.data?.data;
          registerPushNotifications();
          localStorage.setItem('token', token);
          sessionStorage.removeItem('signInEmail');
          sessionStorage.removeItem('signInPassword');
          sessionStorage.removeItem('signUpEmail');
          sessionStorage.removeItem('signUpPassword');
          recheckAuth();
          navigate('/');
        }
        Notification('success', response?.data?.message);
      })
      .catch((err) => {
        setIsLoading(false);
        Notification('error', err.response?.data?.message);
      });
  };

  const isFormValid = () => {
    const { username } = loginDetails;
    return usernameRegex.test(username?.trim());
  };

  return (
    <>
      <div className="card-title">
        <CloseIcon
          iconType="back"
          customStyle="back_btn"
          onClick={() => handleChange('signin')}
        />
        {t('Auth.CreateUserName')}
      </div>
      <div className="inputbox">
        <Input
          label={t('Auth.UserName')}
          type={'text'}
          required
          value={loginDetails.username}
          errMsg={usernameError}
          onChange={(data) => handleFieldChange(data, 'username')}
        />
        <div className="date-container">
          <div className="lebelView">
            <label>
              Enter your D.O.B <span className="requiredSymbol">*</span>
            </label>
          </div>
          <div className="date-select-field">
            <Select
              Options={MonthList}
              value={loginDetails.month}
              onChange={(data) => handleFieldChange(data.target.value, 'month')}
            />
            <Select
              Options={DateList}
              value={loginDetails.day}
              onChange={(data) => handleFieldChange(data.target.value, 'day')}
            />
            <Select
              Options={YearList}
              value={loginDetails.year}
              onChange={(data) => handleFieldChange(data.target.value, 'year')}
            />
          </div>
          <div className="dob-helpertext">{t('Auth.ShownPublicy')}</div>
        </div>
      </div>
      <Button
        label={t('Auth.StartExplore')}
        disabled={!isFormValid()}
        onClick={isFormValid() ? handleSubmit : () => {}}
        customStyles="auth-action-btn"
        loading={isLoading}
        loadingIndicator="Loading…"
      />
      <div className="privacy-container">
        By continuing, you are agree to WafyNet
        <br /> Terms & Conditions and Privacy Policy
      </div>
    </>
  );
};

export default UserNameCreation;
